import { Box, HStack, Link, Stack, VStack } from '@web/components/atoms/Layout'
import { ComponentType } from '@web/graphql-components'
import { type Footer as FooterProps } from '@web/graphql-types'
import { Text } from '../Text'
import { Image } from '../Image'

import { useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import { Logo } from '@web/components/atoms/Logo'
import { useMemo } from 'react'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { useMediaQuery } from '@web/components/utils/chakra-hooks'
import { MenuItemLink } from '../utils'
import { PageButton } from '../PageButton'
import { useMergeFields } from '../Text/MergeTags/MergeTag'
import { CHARITY_MERGE_TAGS } from '../Text/MergeTags/utils'

type StackDirection = 'row' | 'column'

const centerText = (text: string | undefined) => {
  if (!text) {
    return text
  }

  try {
    const transformedText = JSON.parse(text)
    transformedText.root.format = 'center'

    return JSON.stringify(transformedText)
  } catch {
    return text
  }
}

const [CharityName] = CHARITY_MERGE_TAGS

const Footer = (props: FooterProps) => {
  const charityName = useMergeFields((state) => state.mergeFields.get(CharityName.MergeFieldSettingId)) ?? ''
  const [isMobile] = useMediaQuery(['(max-width: 900px)'], { fallback: [] })
  const { colorData } = useBrandingsStore(({ colorData }) => ({
    colorData,
  }))
  const styles = useMemo(() => {
    if (isMobile) {
      const { padding, margin, ...rest } = props?.styles ?? {}

      return {
        ...rest,
        ...props.mobileStyles,
      }
    }

    return props.styles
  }, [props.styles, props.mobileStyles, isMobile])

  const stackCss = {
    direction: isMobile ? 'column' : ('row' as StackDirection),
    paddingBottom: isMobile ? '64px' : '0',
  }

  const { keyInformationText, taxDeductibleText } = useMemo(() => {
    if (!isMobile) {
      return {
        keyInformationText: props.keyInformationText,
        taxDeductibleText: props.taxDeductibleText,
      }
    }

    return {
      keyInformationText: centerText(props.keyInformationText ?? ''),
      taxDeductibleText: centerText(props.taxDeductibleText ?? ''),
    }
  }, [props.keyInformationText, props.taxDeductibleText, isMobile])

  const backgroundColor = useColorWithOpacity(styles?.backgroundColor, '1') ?? '#FFF'
  return (
    <Stack
      w="100%"
      {...(styles?.id ? { id: styles.id } : {})}
      {...(styles?.class ? { className: styles.class } : {})}
      css={{
        backgroundColor: backgroundColor,
      }}
      alignItems="center"
      px="36px"
      py="24px"
    >
      <Stack
        {...stackCss}
        alignItems="center"
        justifyContent="space-between"
        maxW={styles?.maxWidth ? `${styles.maxWidth}px` : '100%'}
        w="100%"
      >
        <Stack {...stackCss} gap="48px" w="100%" alignItems="center">
          <Logo styles={styles} />
          <VStack
            w="100%"
            className="ge-footer__text-container"
            maxW="800px"
            gap={0}
            alignItems={isMobile ? 'center' : 'start'}
            color={styles?.color === 'light' ? colorData['Text light'] : colorData['Text dark']}
          >
            {taxDeductibleText && (
              <Text
                id="tax-deductible-text"
                type={ComponentType.Text}
                textValue={taxDeductibleText}
                styles={{ width: '100%' }}
              />
            )}
            {keyInformationText && (
              <Text
                id="key-information-text"
                type={ComponentType.Text}
                textValue={keyInformationText}
                styles={{ width: '100%' }}
              />
            )}
          </VStack>
        </Stack>
        <HStack>
          <VStack w="fit-content">
            {props.styles?.links?.map((link: MenuItemLink) => {
              if (link.style !== 'standard') {
                return (
                  <Link href={link.url} color="ge.white" key={link.id}>
                    <PageButton
                      id={link.id}
                      name={`${link.style.charAt(0).toUpperCase()}${link.style.slice(1)}` as 'Primary' | 'Secondary'}
                      buttonText={link.label}
                    />
                  </Link>
                )
              }
              return (
                <Link href={link.url} key={link.id}>
                  {link.label}
                </Link>
              )
            })}
          </VStack>
          <Box>
            <Image
              id="giveeasy-logo"
              type={ComponentType.Image}
              url={`/assets/${
                styles?.color === 'light'
                  ? 'PoweredByPrimaryLogo-TransparentBackground-Light.png'
                  : 'PoweredByPrimaryLogo-TransparentBackground.png'
              }`}
              alt="GiveEasy Logo"
              styles={{
                href: `https://www.giveeasy.org/?utm_content=${encodeURIComponent(charityName)}-donation-footer`,
                target: '_blank',
              }}
            />
          </Box>
        </HStack>
      </Stack>
    </Stack>
  )
}

export default Footer
